import { I18n } from "@assets/strings";
import { Exercise } from "@domain/lms/course/exercise/exercise";
import { Card } from "@ui/components/containers/card";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Primary, PrimaryBold } from "@ui/components/containers/primary";
import { checkboxInputCss } from "@ui/components/inputs/checkboxInput";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { Set } from "immutable";
import React, { JSX } from "react";
import styled from "styled-components";

interface ExerciseViewProps extends React.HTMLAttributes<HTMLDivElement> {
	exercise: Exercise;
	index: number;
	preview?: boolean;
	renderMenu?: () => JSX.Element;
	collapsed?: boolean;
	onExtend?: () => void;
	renderCollapser?: () => JSX.Element;
	onAnswerChosen?: (choice: Set<number>) => void;
	currentAnswer: Set<number>;
}
export const ExerciseView: React.FC<ExerciseViewProps> = ({
	exercise,
	index,
	preview,
	renderMenu,
	collapsed = false,
	onExtend,
	renderCollapser,
	currentAnswer,
	onAnswerChosen,
	...htmlProps
}) => {
	const { title, choices, comment, type } = exercise;
	const isReadonly = !!choices.find((c) => !!c.valid) || preview;

	if (collapsed) {
		return (
			<CollapsedContainer {...htmlProps} onClick={() => onExtend && onExtend()}>
				<Primary>
					<b>{I18n.t("course.section.qcm.number", { index: index + 1 })}</b>
				</Primary>
				{renderMenu?.()}
				{renderCollapser?.()}
			</CollapsedContainer>
		);
	}

	return (
		<Container {...htmlProps}>
			<PrimaryBold>{I18n.t("course.section.qcm.number", { index: index + 1 })}</PrimaryBold>
			<Primary>
				<b>{title}</b>
			</Primary>
			<Answers>
				{exercise.type === "MULTIPLE"
					? choices.map(({ id, text, valid }, i) => (
							<Choice key={id} isValid={isReadonly ? valid : undefined}>
								<input
									id={id}
									name={id}
									disabled={isReadonly}
									type="checkbox"
									checked={currentAnswer?.toArray().includes(i) || valid}
									onChange={(e) => {
										const isChecked = e.currentTarget.checked;
										if (type === "UNIQUE") {
											onAnswerChosen?.(isChecked ? Set([i]) : Set());
										} else {
											onAnswerChosen?.(isChecked ? currentAnswer.add(i) : currentAnswer.remove(i));
										}
									}}
								/>
								<label htmlFor={id}>{text}</label>
							</Choice>
						))
					: choices.map(({ id: choiceId, text, valid }, i) => {
							return (
								<Choice key={choiceId} isValid={isReadonly ? valid : undefined}>
									<input
										id={choiceId}
										name={exercise.id}
										disabled={isReadonly}
										type="radio"
										checked={currentAnswer?.toArray().includes(i) || valid}
										onChange={(e) => {
											const isChecked = e.currentTarget.checked;
											onAnswerChosen?.(isChecked ? Set([i]) : Set());
										}}
									/>
									<label htmlFor={choiceId}>{text}</label>
								</Choice>
							);
						})}
			</Answers>
			{comment && comment.hasText() && (
				<Comment contractibleFold fold foldColor={theme.colors.darkgray} content={comment} />
			)}
			{renderMenu?.()}
			{renderCollapser?.()}
		</Container>
	);
};

const CollapsedContainer = styled(Card)`
	position: relative;
	border: none;
	padding: 22px 26px;
`;

const Container = styled(Card)`
	border: none;
	padding: 22px 26px;
	${gap(20)}
	position: relative;
`;

const Answers = styled.div`
	${gap(15)};
`;

const Choice = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isValid",
})<{ isValid?: boolean }>`
	display: flex;
	align-items: flex-start;

	input {
		flex-shrink: 0;
		min-width: 20px;
		height: 20px;
		padding: 5px;
		margin-right: 10px;
		appearance: none;
		-webkit-appearance: none;
		border: 1px solid ${({ theme }) => theme.colors.gray};
		position: relative;

		&[type="checkbox"] {
			${checkboxInputCss};
			margin-right: 10px;
			padding: 0;
			--checkbox-color: ${({ theme, isValid }) =>
				isValid === undefined ? theme.colors.secondary : isValid ? theme.colors.green : theme.colors.red};
		}
		&[type="radio"] {
			border-radius: 10px;
			&::after {
				border-radius: 6px;
			}
		}

		&::after {
			content: "";
			background-color: ${({ theme, isValid }) =>
				isValid === undefined ? theme.colors.secondary : isValid ? theme.colors.green : theme.colors.red};
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			margin: 4px;
			border-radius: 5px;
			transform: scale(0);
		}
		&:checked::after {
			transform: scale(1);
		}
	}

	label {
		${({ theme }) => theme.textStyles.primary};
	}
`;

const Comment = styled(DraftRenderer)`
	margin-top: 16px;
	${({ theme }) => theme.textStyles.primary};
	color: ${({ theme }) => theme.colors.secondary};
`;
